import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer";
import { api } from '../../services/api';
import Swal from 'sweetalert2';
import { FaBell, FaEnvelopeOpen, FaEnvelope, FaChevronLeft, FaChevronRight, FaTimes, FaSearchPlus } from 'react-icons/fa';
import '../Aeflor/convenio.css';

const IMAGE_BASE_URL = '/usuario/notificacao/imagem';
const NOTIFICACAO_BASE_URL = '/usuario/notificacao';
const NOTIFICACAO_TOTAL_URL = '/usuario/notificacoes';
const MESSAGES_PER_PAGE = 20;

const formatDateToDDMMYYYY = (isoDate) => {
    if (!isoDate) return '';
    try {
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return '';

        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();

        return `${day}/${month}/${year}`;
    } catch (e) {
        return '';
    }
};

const Notificacoes = () => {
    const chapa = localStorage.getItem("chapa");
    const [messages, setMessages] = useState([]);
    const [imageMap, setImageMap] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [totalMessages, setTotalMessages] = useState(0);
    const [loading, setLoading] = useState(false);

    const [selectedIndex, setSelectedIndex] = useState(null);
    const [imageZoomed, setImageZoomed] = useState(null);

    const totalPages = Math.ceil(totalMessages / MESSAGES_PER_PAGE);

    const selectedMessage = useMemo(() => {
        return selectedIndex !== null ? messages[selectedIndex] : null;
    }, [selectedIndex, messages]);

    const fetchImage = useCallback(async (codImagem) => {
        const imagemKey = codImagem || 'default';
        if (!chapa) return;

        try {
            const res = await api.get(`${IMAGE_BASE_URL}?usuario=${chapa}&codigo=${codImagem}`);
            const imageDataUri = `data:image/jpeg;base64,${res.data}`;

            setImageMap(prevMap => {
                if (prevMap[imagemKey] && prevMap[imagemKey] !== 'error') return prevMap;
                return { ...prevMap, [imagemKey]: imageDataUri };
            });
        } catch (err) {
            console.error(`Erro ao carregar imagem ${imagemKey}`, err);
            setImageMap(prevMap => ({ ...prevMap, [imagemKey]: 'error' }));
        }
    }, [chapa]);

    const fetchTotalMessages = useCallback(async () => {
        if (!chapa) return;
        try {
            const res = await api.get(`${NOTIFICACAO_TOTAL_URL}?usuario=${chapa}`);
            if (res.data && typeof res.data.total === 'number') {
                setTotalMessages(res.data.total);
            } else {
                setTotalMessages(0);
            }
        } catch (err) {
            console.error("Erro ao carregar total de notificações", err);
            setTotalMessages(0);
        }
    }, [chapa]);

    const fetchMessages = useCallback(async (pageNumber) => {
        if (!chapa) return;

        setLoading(true);
        try {
            const res = await api.get(`${NOTIFICACAO_BASE_URL}?usuario=${chapa}&pagina=${pageNumber}`);
            const newMessages = Array.isArray(res.data) ? res.data : [];

            if (newMessages.length > 0) {
                setMessages(newMessages);
                newMessages.forEach(msg => {
                    if (msg.imagem) fetchImage(msg.imagem);
                });
            } else {
                setMessages([]);
            }
        } catch (err) {
            console.error("Erro ao carregar notificações", err);
            Swal.fire({
                icon: "error",
                title: "Erro",
                text: "Não foi possível carregar as notificações.",
                confirmButtonColor: "#8b0000"
            });
            setMessages([]);
        } finally {
            setLoading(false);
        }
    }, [chapa, fetchImage]);

    useEffect(() => {
        fetchTotalMessages();
    }, [fetchTotalMessages]);

    useEffect(() => {
        if (chapa && currentPage > 0) {
            fetchMessages(currentPage);
        }
    }, [chapa, currentPage, fetchMessages]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber !== currentPage && pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            setSelectedIndex(null);
        }
    };

    const handleReadAndOpen = useCallback(async (index, message) => {
        setSelectedIndex(index);

        if (message.status === 'E') {
            try {
                const formattedDate = formatDateToDDMMYYYY(message.datamsg);

                await api.post(
                    `${NOTIFICACAO_BASE_URL}?usuario=${chapa}&codigo=${message.codmsg}&tipo=${message.tipo}&datamsg=${formattedDate}`
                );

                setMessages(prevMessages =>
                    prevMessages.map(msg =>
                        msg.codmsg === message.codmsg ? { ...msg, status: 'L' } : msg
                    )
                );

                const currentMsgCount = parseInt(localStorage.getItem("msg") || "0", 10);
                if (currentMsgCount > 0) {
                    const newMsgCount = currentMsgCount - 1;
                    localStorage.setItem("msg", String(newMsgCount));
                }

            } catch (err) {
                console.error("Erro ao marcar mensagem como lida", err);
            }
        }
    }, [chapa]);

    const handleImageZoom = useCallback((e, imageSource) => {
        e.stopPropagation();
        if (imageSource && imageSource !== 'error') {
            setImageZoomed(imageSource);
        }
    }, []);

    const handleCloseModal = () => {
        setSelectedIndex(null);
    };

    const goToNextMessage = () => {
        if (selectedIndex !== null && selectedIndex < messages.length - 1) {
            const nextIndex = selectedIndex + 1;
            handleReadAndOpen(nextIndex, messages[nextIndex]);
        }
    };

    const goToPrevMessage = () => {
        if (selectedIndex !== null && selectedIndex > 0) {
            const prevIndex = selectedIndex - 1;
            handleReadAndOpen(prevIndex, messages[prevIndex]);
        }
    };

    const formatDate = (isoDate) => {
        if (!isoDate) return 'N/A';
        const date = new Date(isoDate);
        return date.toLocaleDateString('pt-BR');
    };

    const truncateMessage = (text, maxLength = 100) => {
        if (!text) return '';
        const cleanedText = text.replace(/\\n/g, ' ').replace(/\s\s+/g, ' ');
        return cleanedText.length > maxLength ? cleanedText.substring(0, maxLength) + '...' : cleanedText;
    };

    const PaginationControls = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <nav className="d-flex justify-content-center mt-4">
                <ul className="pagination pagination-sm gap-1">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link rounded-2 fw-semibold"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || loading}
                        >
                            <FaChevronLeft size={10} />
                        </button>
                    </li>

                    {pages.map(number => (
                        <li key={`page-${number}`} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                            <button
                                className={`page-link rounded-2 fw-bold ${number === currentPage ? 'bg-danger border-danger text-white' : ''}`}
                                onClick={() => handlePageChange(number)}
                                disabled={loading}
                            >
                                {number}
                            </button>
                        </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link rounded-2 fw-semibold"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || loading}
                        >
                            <FaChevronRight size={10} />
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    const MessageList = () => (
        <div className={`d-flex flex-column gap-2 ${loading && messages.length > 0 ? 'opacity-50' : ''}`}>
            {messages.map((message, index) => {
                const isUnread = message.status === 'E';
                const imageSource = message.imagem ? imageMap[message.imagem] : null;
                const uniqueKey = `${message.codmsg || 'no-id'}-${message.linha || index}`;

                return (
                    <div
                        key={uniqueKey}
                        className={`p-3 rounded-4 border bg-white shadow-sm d-flex align-items-center justify-content-between transition-all ${isUnread ? 'border-start border-4 border-danger' : ''}`}
                        onClick={() => handleReadAndOpen(index, message)}
                        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                        <div className="d-flex align-items-center gap-3">
                            {/* Avatar/Imagem da Notificação */}
                            <div className="flex-shrink-0">
                                {imageSource && imageSource !== 'error' ? (
                                    <img
                                        src={imageSource}
                                        alt="Notificação"
                                        className="rounded-circle border shadow-sm"
                                        style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div
                                        className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${isUnread ? 'bg-danger text-white' : 'bg-light text-secondary border'}`}
                                        style={{ width: '48px', height: '48px', fontSize: '18px' }}
                                    >
                                        {isUnread ? <FaEnvelope /> : <FaEnvelopeOpen />}
                                    </div>
                                )}
                            </div>

                            {/* Conteúdo resumido */}
                            <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <h6 className={`m-0 fw-bold ${isUnread ? 'text-dark' : 'text-secondary'}`}>
                                        {message.assunto}
                                    </h6>
                                    {isUnread && (
                                        <span className="badge bg-danger text-white extra-small px-2 py-0.5 rounded-pill">
                                            Nova
                                        </span>
                                    )}
                                </div>
                                <p className="text-muted small m-0 mt-1 line-clamp-2">
                                    {truncateMessage(message.mensagem)}
                                </p>
                            </div>
                        </div>

                        {/* Data */}
                        <div className="text-end flex-shrink-0 ms-2">
                            <small className="text-muted fw-semibold extra-small d-block">
                                {formatDate(message.datamsg)}
                            </small>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const ImageZoomModal = ({ imageSource, onClose }) => {
        if (!imageSource) return null;

        return (
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 2000 }}
                onClick={onClose}
            >
                <div
                    className="modal-dialog modal-fullscreen-sm-down modal-xl h-100"
                    role="document"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content bg-transparent border-0 h-100">
                        <div className="modal-header border-0 p-2 d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                aria-label="Fechar"
                                onClick={onClose}
                            ></button>
                        </div>
                        <div className="modal-body p-0 d-flex align-items-center justify-content-center">
                            <img
                                src={imageSource}
                                alt="Imagem em tela cheia"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const MessageModal = () => {
        if (!selectedMessage) return null;

        const imageSource = selectedMessage.imagem ? imageMap[selectedMessage.imagem] : null;
        const isFirst = selectedIndex === 0;
        const isLast = selectedIndex === messages.length - 1;

        const handleBackdropClick = (e) => {
            if (e.target === e.currentTarget) {
                handleCloseModal();
            }
        };

        return (
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto', zIndex: 1050, backdropFilter: 'blur(3px)' }}
                onClick={handleBackdropClick}
            >
                <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                    <div className="modal-content rounded-4 border-0 shadow-lg overflow-hidden">
                        <div className="modal-header text-white border-0 p-3 p-md-4" style={{ background: 'linear-gradient(135deg, #8b0000 0%, #a91b0d 50%, #c0392b 100%)' }}>
                            <div>
                                <h5 className="modal-title fw-bold m-0 text-white">{selectedMessage.assunto}</h5>
                                <small className="text-white-50">Enviada em {formatDate(selectedMessage.datamsg)}</small>
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                aria-label="Fechar"
                                onClick={handleCloseModal}
                            ></button>
                        </div>

                        <div className="modal-body p-3 p-md-4" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            {imageSource && imageSource !== 'error' && (
                                <div
                                    className="text-center mb-3 position-relative rounded-3 overflow-hidden border"
                                    onClick={(e) => handleImageZoom(e, imageSource)}
                                    style={{ cursor: 'zoom-in' }}
                                >
                                    <img
                                        src={imageSource}
                                        alt={selectedMessage.assunto}
                                        style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px' }}
                                    />
                                    <span className="badge bg-dark bg-opacity-75 text-white position-absolute bottom-0 end-0 m-2 d-flex align-items-center gap-1 extra-small">
                                        <FaSearchPlus /> Ampliar
                                    </span>
                                </div>
                            )}

                            <div className="text-dark fs-6" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                                {selectedMessage.mensagem}
                            </div>
                        </div>

                        {/* RODAPÉ AJUSTADO PARA MOBILE (LADO A LADO SEM QUEBRA) */}
                        <div className="modal-footer border-top bg-light p-2 p-md-3 d-flex align-items-center justify-content-between flex-nowrap gap-1 gap-md-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary fw-semibold rounded-3 px-2 px-md-3 text-nowrap flex-fill"
                                style={{ fontSize: '0.8rem' }}
                                onClick={goToPrevMessage}
                                disabled={isFirst}
                            >
                                ← Anterior
                            </button>

                            <button
                                type="button"
                                className="btn btn-sm btn-secondary fw-semibold rounded-3 px-2 px-md-4 text-nowrap flex-fill"
                                style={{ fontSize: '0.8rem' }}
                                onClick={handleCloseModal}
                            >
                                Fechar
                            </button>

                            <button
                                type="button"
                                className="btn btn-sm btn-danger fw-semibold rounded-3 px-2 px-md-3 text-nowrap flex-fill"
                                style={{
                                    background: 'linear-gradient(135deg, #8b0000 0%, #c0392b 100%)',
                                    border: 'none',
                                    fontSize: '0.8rem'
                                }}
                                onClick={goToNextMessage}
                                disabled={isLast}
                            >
                                Próxima →
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    return (
        <div className="bg-light min-vh-100" style={{ paddingTop: '80px', paddingBottom: '40px' }}>
            <Menu modulo="ARBOR" />

            <div
                className="container-fluid py-3 px-3 px-md-5"
                style={{ maxWidth: '1000px', margin: '0 auto' }}
            >
                {/* Título da Página */}
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                    <div>
                        <h4 className="fw-bold m-0 d-flex align-items-center text-dark">
                            <FaBell className="me-2 text-danger" />
                            Minhas Notificações
                        </h4>
                        <small className="text-muted">Acompanhe avisos e recados da empresa</small>
                    </div>

                    <span className="badge bg-white text-dark border shadow-sm px-3 py-2 rounded-3 fw-bold">
                        Total: <strong className="text-danger">{totalMessages}</strong>
                    </span>
                </div>

                {/* Conteúdo */}
                {loading && currentPage === 1 && messages.length === 0 && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-danger" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        <p className="mt-2 text-muted small">Carregando mensagens...</p>
                    </div>
                )}

                {messages.length === 0 && !loading && (
                    <div className="card border-0 shadow-sm rounded-4 p-5 text-center bg-white">
                        <FaBell className="text-muted opacity-50 mb-3 mx-auto" size={48} />
                        <h5 className="fw-bold text-dark mb-1">Você não tem notificações</h5>
                        <p className="text-muted small mb-0">Quando o RH ou a empresa enviarem novos comunicados, eles aparecerão nesta lista.</p>
                    </div>
                )}

                {messages.length > 0 && (
                    <>
                        <MessageList />
                        <PaginationControls />
                    </>
                )}
            </div>

            <Footer />

            {selectedMessage && <MessageModal />}
            {imageZoomed && <ImageZoomModal imageSource={imageZoomed} onClose={() => setImageZoomed(null)} />}
        </div>
    );
};

export default Notificacoes;